import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { CompanyDashboardComponent } from 'components/company/company-dashboard.component';
import { NotificationPopupComponent, NotificationPopupErrorComponent } from 'components/core/notification-popup/notification-popup.component';
import { IconType } from 'components/layout/icon/icon.component';
import { ViewTaskflowComponent } from 'components/task/taskflow/view-taskflow.component';
import { NotificationStatus } from 'enums/NotificationStatus';
import { environment } from "environments/environment";
import { NotificationModel } from 'models/generated/Communication.Api.Models.Notification';
import { TranslatePipe } from 'pipes/translate.pipe';
import { firstValueFrom, Observable } from 'rxjs';
import { NavigationService } from 'services/navigation.service';
import { UserService } from 'services/user.service';

@Injectable()
export class NotificationService {
	private baseUrl = `${environment.gatewayUrl}/api/notifications`;
	public statusMessages: { languageVariable: string, icon?: IconType, text?: string }[] = [];

	constructor(private http: HttpClient,
		private translate: TranslatePipe,
		private userService: UserService,
		private navigation: NavigationService) { }

	success(languageVariable: string) {
		this.navigation.openPopup(NotificationPopupComponent).then(view => {
			view.title = this.translate.transform('popup-success');
			view.text = this.translate.transform(languageVariable);
		});
	}

	warning(languageVariable: string) {
		this.navigation.openPopup(NotificationPopupComponent).then(view => {
			view.title = this.translate.transform('popup-warning');
			view.text = this.translate.transform(languageVariable);
		});
	}

	status(languageVariable: string, icon?: IconType, text?: string) {
		this.statusMessages.push({ languageVariable, icon, text });
	}
	
	error(languageVariable: string, error: string, stackTrace?: string, operationId?: string) {
		this.navigation.openPopup(NotificationPopupErrorComponent).then(view => {
			view.title = this.translate.transform('popup-error');
			view.text = this.translate.transform(languageVariable, { 'error': error });
			if (operationId) view.details = this.translate.transform('popup-error.operationId', { 'operationId': operationId });
			
			if (stackTrace) {
				view.stacktrace = [];
				for (const line of stackTrace.split('\n')) {
					view.stacktrace.push({
						text: line,
						isOwnCode: line.match(/\:line\s\d+/g) != null
					});
				}
			}
		});
	}

	confirm(languageVariable: string, textVariables: any = undefined, title: string | undefined = undefined): Observable<boolean> {
		return new Observable<boolean>(observer => {
			this.navigation.openPopup(NotificationPopupComponent, 'general.confirm').then(view => {
				view.title = title ? this.translate.transform(title, textVariables) : this.translate.transform('popup-warning');
				view.text = this.translate.transform(languageVariable, textVariables);
				view.buttons = [{ text: 'general.cancel', type: 'ghost', value: false }, { text: 'general.ok', type: 'confirm', value: true }];
				view.result.subscribe(result => {
					if (result) observer.next(true);
					else observer.next(false);
					observer.complete();
				});
			});
		});

	}

	getNotification(notificationId: string): Promise<NotificationModel> {
		return firstValueFrom(this.http.get<NotificationModel>(`${this.baseUrl}/${notificationId}`));
	}

	getActiveNotifications(): Promise<NotificationModel[]> {
		return firstValueFrom(this.http.get<NotificationModel[]>(`${this.baseUrl}`));
	}

	updateNotificationStatus(id: string, status: NotificationStatus): Promise<NotificationModel> {
		return firstValueFrom(this.http.put<NotificationModel>(`${this.baseUrl}/${id}/status`, status));
	}

	navigateToNotificationSource(notification: NotificationModel): boolean {

		if (notification.status == NotificationStatus.New && notification.assignedToUserId == this.userService.getCurrentUser().id) {
			notification.status = NotificationStatus.Read;
			this.updateNotificationStatus(notification.id!, NotificationStatus.Read);
		}

		const taskflowId = notification.communicationLogEntry?.taskflowId;
		const companyId = notification.communicationLogEntry?.companyId;

		if (taskflowId) {
			this.navigation.refreshView(ViewTaskflowComponent).then(view => {
				view.selectedActionId = notification.communicationLogEntry?.taskActionId;
				view.taskflowId = taskflowId;
			});
			return true;
		}

		if (companyId) {
			this.navigation.refreshView(CompanyDashboardComponent).then(view => { view.companyId = companyId; });
			return true;
		}

		return false;
	}
}
